import axios from "axios";
import { snakeCase } from "lodash";
import { pipe } from "lodash/fp";
import { useToast } from "primevue/usetoast";
import { ref } from "vue";

import { applications } from "@/api";
import { stripPhoneNumber, toRailsDateTime } from "@/utilities";
import { useForm } from "@/utilities/validations";

import { Props } from "./Index.vue";
import { Application } from "./types";

const maybeApply = <T, V extends NonNullable<T>, R>(v: T, fn: (v: V) => R) =>
  v ? fn(v as V) : v;

const sanatizedContactPhones = (app: Application) =>
  (
    [
      "guardian1CellPhone",
      "guardian1HomePhone",
      "guardian1OtherPhone",
      "guardian1WorkPhone",
      "guardianPhone1",
      "guardianPhone2",
      "guardianPhone3",
      "guardianPhone4",
      "emergencyContact1Phone1",
      "emergencyContact1Phone2",
      "emergencyContact1Phone3",
    ] as Array<keyof Application>
  ).reduce((acc, field) => {
    const phone = app[field] as
      | undefined
      | { num: string; type: string | undefined };

    if (phone == null) {
      return {
        ...acc,
        [field]: phone,
      };
    }

    return {
      ...acc,
      [field]: { ...phone, num: stripPhoneNumber(phone.num) },
    };
  }, {});

const sanatizeApplication = pipe((app: Application) => ({
  ...app,
  ...sanatizedContactPhones(app),
  homephone: stripPhoneNumber(app.homephone || ""),
  cellphone: stripPhoneNumber(app.cellphone || ""),
  summerphone: stripPhoneNumber(app.summerphone || ""),
  arrivalDt: maybeApply(app.arrivalDt, toRailsDateTime),
  priority1: maybeApply(app.priority1, snakeCase),
  priority2: maybeApply(app.priority2, snakeCase),
  priority3: maybeApply(app.priority3, snakeCase),
}));

export function useSubmission(form: Application, props: Readonly<Props>) {
  const loading = ref(false);
  const toast = useToast();

  const formName = "application-form";
  const { submit: submitForm, setFieldErrorMessage } = useForm(formName);

  const showFormError = () => {
    toast.add({
      severity: "error",
      summary: "Errors in form",
      detail: "You seem to have a few errors in your form",
      life: 5000,
    });
  };

  const submit = submitForm(
    async () => {
      loading.value = true;

      const application = sanatizeApplication(form);

      try {
        let result;

        if (props.formType === "new") {
          result = await applications.create({
            data: { application },
          });
        } else {
          result = await applications.update({
            params: { id: application.appId },
            data: { application },
          });
        }

        window.location.assign(result.location);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          if (err.response.data.location) {
            window.location.assign(err.response.data.location);
          } else {
            Object.entries(err.response.data).forEach(([key, val]) => {
              setFieldErrorMessage(key, (val as string[]).join(", "));
            });
            showFormError();
          }
        }
      } finally {
        loading.value = false;
      }
    },
    async () => showFormError(),
  );

  return { submit, loading, formName };
}
